import {MinusOutlined, PlusOutlined} from '@ant-design/icons';
import {BottomSheet, Button} from '@ds/components';
import {cn} from '@ds/helpers';
import {toast} from '@Toast';
import {useState} from 'react';
import {useAppDispatch} from 'store';
import {updateBulkProductDiscount} from 'store/slices/custom-deal';

import {CUSTOM_DEAL_PRESET_DISCOUNTS} from '../../../constants';
import {getCustomDealVendorId} from '../../../utils';

type SetBulkDiscountModalProps = {
  show: boolean;
  onClose: () => void;
  selectedProductIds: Set<number | string>;
  onSuccess?: () => void;
  currentDealId?: string | number;
};

const SetBulkDiscountModal: React.FC<SetBulkDiscountModalProps> = ({
  show,
  onClose,
  selectedProductIds,
  onSuccess,
  currentDealId
}) => {
  const dispatch = useAppDispatch();
  const vendorId = getCustomDealVendorId();
  const [selectedDiscount, setSelectedDiscount] = useState<number | 'custom' | null>(null);
  const [customDiscount, setCustomDiscount] = useState<number | null>(null);

  const handleConfirm = async () => {
    if (!currentDealId || selectedProductIds.size === 0) {
      return;
    }

    const finalDiscount = selectedDiscount === 'custom' ? customDiscount : selectedDiscount;

    try {
      const response = (await dispatch(
        updateBulkProductDiscount({
          dealId: currentDealId,
          vendorId,
          productIds: selectedProductIds,
          discount: finalDiscount
        })
      ).unwrap()) as {errors?: {message?: string}};

      if (response?.errors?.message) {
        toast(response.errors.message);
        return;
      }
      toast('تخفیف گروهی با موفقیت اعمال شد');
      onSuccess?.();
      onClose();
    } catch {
      toast('خطایی در برقراری ارتباط با سرور رخ داد');
    }
  };

  return (
    <BottomSheet
      show={show}
      onClose={onClose}
      contentStyle={{padding: '24px 16px', maxHeight: '90vh'}}
      header={
        <div className="flex flex-col items-center px-4 py-2 text-subtitle-md text-neutral-fg">
          <span className="-mt-6 mb-4 h-1 w-8 rounded-full bg-gray-03" aria-hidden />
          <span>تخفیف گروهی</span>
        </div>
      }
      canceledOnTouchOutside
    >
      <div className="flex w-full flex-col">
        <div className="flex flex-col pt-2">
          <div className="flex flex-col">
            <span className="mb-2 text-body-sm text-gray-05">تعداد محصول</span>
            <span className="text-body-sm-emphasis text-neutral-fg">
              {selectedProductIds.size} کالا
            </span>
          </div>
          <div className="my-3 h-px bg-gray-02" />
          <div className="flex flex-col">
            <span className="mb-2 text-body-sm text-gray-05">نوع تخفیف</span>
            <span className="text-body-sm-emphasis text-neutral-fg">تخفیف سوپرمارکت شما</span>
          </div>
          <div className="my-3 h-px bg-gray-02" />
        </div>
        <div className="mb-8 flex flex-col">
          <h3 className="mb-4 text-right text-subtitle-md text-neutral-fg">سهم شما</h3>
          <div className="mb-2 flex justify-end gap-3">
            {CUSTOM_DEAL_PRESET_DISCOUNTS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={cn(
                  'flex min-w-0 flex-1 flex-col items-center justify-center rounded border border-gray-03 bg-white px-4 py-2',
                  selectedDiscount === preset && 'border-orange-deep bg-orange-wash text-orange-deep'
                )}
                onClick={() => setSelectedDiscount(preset)}
              >
                <span className="text-body-sm-emphasis">٪{preset}</span>
              </button>
            ))}
          </div>
          <div className="flex flex-col items-center">
            <div
              className={cn(
                'flex w-full items-center justify-center gap-3 rounded-lg py-1',
                selectedDiscount === 'custom' && 'bg-orange-cream'
              )}
            >
              <button
                className="flex size-10 items-center justify-center rounded-full border border-gray-03 bg-white"
                onClick={() => {
                  setCustomDiscount((current) => Math.min((current ?? 0) + 1, 100));
                  setSelectedDiscount('custom');
                }}
                type="button"
                aria-label="افزایش تخفیف"
              >
                <PlusOutlined aria-hidden />
              </button>
              <div className="relative flex w-30 items-center">
                <input
                  type="number"
                  className="h-12 w-full rounded-lg border border-gray-03 px-3 text-center text-subtitle-md text-neutral-fg"
                  value={customDiscount ?? ''}
                  onChange={(event) => {
                    const value = parseInt(event.target.value, 10) || 0;
                    if (value >= 0 && value <= 100) {
                      setCustomDiscount(value);
                      setSelectedDiscount('custom');
                    }
                  }}
                  onFocus={() => setSelectedDiscount('custom')}
                  min={0}
                  max={100}
                />
                <span className="pointer-events-none absolute inset-s-3 text-subtitle-md text-gray-07">
                  %
                </span>
              </div>
              <button
                className="flex size-10 items-center justify-center rounded-full border border-gray-03 bg-white"
                onClick={() => {
                  setCustomDiscount((current) => Math.max((current ?? 0) - 1, 0));
                  setSelectedDiscount('custom');
                }}
                type="button"
                aria-label="کاهش تخفیف"
              >
                <MinusOutlined aria-hidden />
              </button>
            </div>
          </div>
        </div>
        <div className="mt-auto flex w-full flex-col gap-2">
          <Button
            fullWidth
            size="lg"
            onClick={() => void handleConfirm()}
            disabled={(selectedDiscount === 'custom' && !customDiscount) || selectedDiscount === null}
          >
            تایید
          </Button>
          <Button variant="ghost" size="lg" fullWidth onClick={onClose}>
            بستن
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default SetBulkDiscountModal;
